"use client";

import { c as _c } from "react/compiler-runtime";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type CSSProperties } from "react";
import type { Swiper as SwiperInstance } from "swiper";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import CarouselArrow from "./CarouselArrow";
import SubmissionArtworkCard from "./SubmissionArtworkCard";
export interface SubmissionCarouselHandle {
  pauseAutoplay: () => void;
  resumeAutoplay: () => void;
}
interface SubmissionCarouselProps {
  readonly onActiveDropChange?: (drop: ExtendedDrop | null) => void;
}
const SubmissionCarousel = forwardRef<SubmissionCarouselHandle, SubmissionCarouselProps>(function SubmissionCarousel(t0, ref) {
  const $ = _c(37);
  const {
    onActiveDropChange
  } = t0;
  const {
    seizeSettings,
    isLoaded
  } = useSeizeSettings();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const waveId = seizeSettings.memes_wave_id;
  const t1 = waveId ?? "";
  const t2 = !waveId;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      waveId: t1,
      sort: WaveDropsLeaderboardSort.RATING_PREDICTION,
      pausePolling: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    drops,
    isFetching
  } = useWaveDropsLeaderboard(t3);
  const swiperRef = useRef(null);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => ({
      pauseAutoplay: () => swiperRef.current?.autoplay.pause(),
      resumeAutoplay: () => swiperRef.current?.autoplay.resume()
    });
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  useImperativeHandle(ref, t4);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      "--carousel-inactive-scale": "0.68",
      "--carousel-gap": "4px",
      "--carousel-arrow-offset": "calc((var(--carousel-card-width) / 2) + var(--carousel-gap) + (var(--carousel-card-width) * var(--carousel-inactive-scale) / 2))"
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const carouselVars = t5 as CSSProperties;
  let t6;
  if ($[5] !== drops) {
    const filtered = drops.filter(_temp);
    const hashCode = _temp2;
    let t7;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = (a, b) => hashCode(a.id) - hashCode(b.id);
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    t6 = [...filtered].sort(t7);
    $[5] = drops;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const shuffledDrops = t6;
  const activeDrop = shuffledDrops[activeIndex] ?? shuffledDrops[0] ?? null;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = swiper => {
      setActiveIndex(swiper.activeIndex);
      setCanScrollLeft(!swiper.isBeginning);
      setCanScrollRight(!swiper.isEnd);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleSlideChange = t7;
  let t8;
  let t9;
  if ($[9] !== activeDrop || $[10] !== onActiveDropChange) {
    t8 = () => {
      onActiveDropChange?.(activeDrop);
    };
    t9 = [activeDrop, onActiveDropChange];
    $[9] = activeDrop;
    $[10] = onActiveDropChange;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[13] !== activeIndex || $[14] !== shuffledDrops.length) {
    t10 = () => {
      if (activeIndex >= shuffledDrops.length && shuffledDrops.length > 0) {
        setActiveIndex(0);
        swiperRef.current?.slideTo(0, 0);
      }
    };
    t11 = [activeIndex, shuffledDrops.length];
    $[13] = activeIndex;
    $[14] = shuffledDrops.length;
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = direction => {
      const swiper_0 = swiperRef.current;
      if (!swiper_0) {
        return;
      }
      if (direction === "left") {
        swiper_0.slidePrev();
      } else {
        swiper_0.slideNext();
      }
    };
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const scroll = t12;
  if (!isLoaded || !waveId) {
    return null;
  }
  if (isFetching && shuffledDrops.length === 0) {
    let t13;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="tw-flex tw-h-64 tw-items-center tw-justify-center"><div className="tw-text-sm tw-text-iron-500">Loading...</div></div>;
      $[18] = t13;
    } else {
      t13 = $[18];
    }
    return t13;
  }
  if (shuffledDrops.length === 0) {
    return null;
  }
  let t13;
  if ($[19] !== canScrollLeft) {
    t13 = true && <CarouselArrow direction="left" onClick={() => scroll("left")} disabled={!canScrollLeft} style={{
      left: "var(--arrow-left-pos)"
    }} className="[--arrow-left-pos:2rem] md:[--arrow-left-pos:calc(50%-var(--carousel-arrow-offset))]" />;
    $[19] = canScrollLeft;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [Autoplay];
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== hasTouchScreen) {
    t15 = hasTouchScreen ? false : {
      delay: 4000,
      disableOnInteraction: false
    };
    $[22] = hasTouchScreen;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = swiper_1 => {
      swiperRef.current = swiper_1;
      handleSlideChange(swiper_1);
    };
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== activeIndex || $[26] !== shuffledDrops) {
    t17 = shuffledDrops.map((drop_0, index) => {
      const isActive = index === activeIndex;
      const distance = Math.abs(index - activeIndex);
      return <SwiperSlide key={drop_0.id} className="tw-flex tw-h-full tw-items-center tw-justify-center tw-overflow-visible" style={{
        width: "var(--carousel-card-width)"
      }}><div role="button" tabIndex={0} onClick={() => swiperRef.current?.slideTo(index)} onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            swiperRef.current?.slideTo(index);
          }
        }} className="tw-h-auto tw-w-full tw-aspect-[4/5] tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400 md:tw-aspect-auto md:tw-h-full" style={{
          opacity: isActive ? 1 : 0.22,
          filter: isActive ? "none" : "grayscale(100%) blur(6px)",
          zIndex: isActive ? 10 : 10 - distance,
          transition: "transform 0.45s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.45s cubic-bezier(0.25, 1, 0.5, 1)",
          transform: isActive ? "translate3d(0, -8px, 0) scale(1)" : "translate3d(0, 0, 0) scale(0.68)",
          transformOrigin: "center",
          willChange: distance <= 1 ? "transform, opacity" : "auto"
        }}><SubmissionArtworkCard drop={drop_0} isActive={isActive} /></div></SwiperSlide>;
    });
    $[25] = activeIndex;
    $[26] = shuffledDrops;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t15 || $[29] !== t17) {
    t18 = <Swiper className="submission-carousel-swiper tw-h-full tw-overflow-hidden tw-overscroll-contain tw-touch-pan-y" modules={t14} slidesPerView="auto" centeredSlides={true} spaceBetween={4} speed={450} initialSlide={0} autoplay={t15} touchEventsTarget="container" touchStartPreventDefault={false} preventInteractionOnTransition={true} onSwiper={t16} onSlideChange={handleSlideChange}>{t17}</Swiper>;
    $[28] = t15;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== canScrollRight) {
    t19 = true && <CarouselArrow direction="right" onClick={() => scroll("right")} disabled={!canScrollRight} style={{
      left: "var(--arrow-right-pos)",
      right: "var(--arrow-right-r)"
    }} className="[--arrow-right-pos:auto] [--arrow-right-r:-0.5rem] md:[--arrow-right-pos:calc(50%+var(--carousel-arrow-offset))] md:[--arrow-right-r:auto]" />;
    $[31] = canScrollRight;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t13 || $[34] !== t18 || $[35] !== t19) {
    t20 = <div className="tw-relative tw-h-full tw-min-h-0 tw-@container [--carousel-card-width:clamp(220px,80%,440px)] md:[--carousel-card-width:clamp(160px,50%,440px)]" style={carouselVars}><div className="tw-mx-auto tw-flex tw-h-full tw-w-full tw-flex-col"><div className="tw-relative tw-min-h-0 tw-flex-1 tw-overflow-hidden">{t13}{t18}{t19}</div></div></div>;
    $[33] = t13;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  return t20;
});
export default SubmissionCarousel;
function _temp(drop) {
  return (drop.parts[0]?.media.length ?? 0) > 0;
}
function _temp2(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash | 0;
  }
  return hash;
}